/**
 * Where a selection sits in a Pages container, and whether the host's Shared
 * context is the one the user is authoring in. Editor session state: nothing
 * here is saved.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { splitPageChildren } from './pagesModel';

export interface PageContext {
    container: SceneObject;
    pages: Pages;
    /** The page showing, by the container's active index; null when it has no pages. */
    activePage: SceneObject | null;
    activeIndex: number;
    /** The page the selection is on; null for the container itself and for shared content, which is on no page. */
    selectedPage: SceneObject | null;
    /** `selectedPage`'s index, or -1. */
    selectedPageIndex: number;
    pageCount: number;
}

/**
 * The Pages context of `selected`: the container it is, or the nearest one
 * above it. A container with no pages still has one, so a host can offer to
 * add the first; so does a `firstChild` container, with no pages (see
 * {@link pageChildrenOf}). Null outside any container.
 */
export function resolvePageContext(selected: SceneObject): PageContext | null {
    let child: SceneObject | null = null;
    for (let node: SceneObject | null = selected; node; child = node, node = node.getParent()) {
        const pages = node.getBehavior(Pages);
        if (!pages) continue;
        const pageChildren = pageChildrenOf(node);
        const activeIndex = pages.getActivePageIndex();
        const selectedPageIndex = child ? pageChildren.indexOf(child) : -1;
        return {
            container: node,
            pages,
            activePage: pageChildren[activeIndex] ?? pageChildren[0] ?? null,
            activeIndex,
            selectedPage: selectedPageIndex >= 0 ? pageChildren[selectedPageIndex] : null,
            selectedPageIndex,
            pageCount: pageChildren.length,
        };
    }
    return null;
}

/**
 * The container's pages: its direct children minus the ones `shared` lists.
 * None for a container without Pages, or in `firstChild` mode, whose pages
 * are its first child's nodes and no scene objects of their own.
 */
export function pageChildrenOf(container: SceneObject): SceneObject[] {
    const data = container.getBehavior(Pages)?.getData();
    if (!data || data.pageSource === 'firstChild') return [];
    return splitPageChildren(
        container.getChildren(),
        data.shared,
        path => container.traceScene.resolveSceneObject(path, container),
    ).pageChildren;
}

/**
 * What an edit of a container itself reaches: the page it shows; its shared
 * children instead, while the user is authoring in its Shared context
 * ({@link isSharedIntent}); every page and shared child while it shows all
 * pages. Empty when nothing is in reach, such as a container with no pages,
 * or an active index past its pages: unlike {@link resolvePageContext}, which
 * falls back to the first page for navigation, an edit is not redirected to a
 * page that isn't showing.
 * Null when `target` is no container, or a `firstChild` one, whose pages are
 * no scene objects: an edit of it reaches its whole subtree.
 */
export function pageScopedContentRoots(target: SceneObject): SceneObject[] | null {
    const pages = target.getBehavior(Pages);
    const data = pages?.getData();
    if (!pages || !data || data.pageSource === 'firstChild') return null;
    const { pageChildren, sharedChildren } = splitPageChildren(
        target.getChildren(),
        data.shared,
        path => target.traceScene.resolveSceneObject(path, target),
    );
    if (isSharedIntent(target)) return sharedChildren;
    if (pages.isShowingAll()) return [...pageChildren, ...sharedChildren];
    const page = pageChildren[pages.getActivePageIndex()];
    return page ? [page] : [];
}

const sharedIntents = new WeakMap<SceneObject, boolean>();

/**
 * Record whether the user is authoring the container's shared content (the
 * host's Shared card) rather than a page. Changes no visibility.
 */
export function setSharedIntent(container: SceneObject, on: boolean): void {
    sharedIntents.set(container, on);
}

/** Whether the user last chose the container's Shared context. */
export function isSharedIntent(container: SceneObject): boolean {
    return sharedIntents.get(container) ?? false;
}
